import {hasRole} from '../../../../utils/hasRole';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import axios from 'axios';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Button} from 'primereact/button';
import {Dialog} from 'primereact/dialog';
import {InputNumber} from 'primereact/inputnumber';
import {Message} from 'primereact/message';

const headers = () => ({Authorization: `Bearer ${localStorage.getItem('userToken')}`, 'App-Network': localStorage.getItem('networkId')});
const url = path => `${process.env.REACT_APP_API_HOST}/api/v2/production-job${path}`;

export default function ProductionTaskList({detailedSearchQuery = '', onJobClick, onSaved, renderStatus}) {
  const {t, i18n} = useTranslation('common');
  const [page, setPage] = useState({first: 0, rows: Number(process.env.REACT_APP_LIST_ROW_DEFAULT) || 20, sortField: 'id', sortOrder: -1});
  const [result, setResult] = useState({items: [], total_item_count: 0});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [editing, setEditing] = useState(null);
  const [minutes, setMinutes] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveErrorDetail, setSaveErrorDetail] = useState('');
  const [discard, setDiscard] = useState(false);
  const network = localStorage.getItem('networkId');
  const canEdit = hasRole('ROLE_PRODUCTION_PLAN_WRITE');
  const dirty = editing && minutes !== (editing.standard_time ?? null);
  const label = key => t(`productionTaskList.${key}`);
  useEffect(() => { setPage(p => ({...p, first: 0})); }, [detailedSearchQuery, network]);
  useEffect(() => {
    const controller = new AbortController();
    const query = window.App.transformQueryParams({...page, page: Math.floor(page.first / page.rows)}) || '';
    setLoading(true); setError('');
    axios.get(url(`?${query.replace(/^\?/, '')}&${detailedSearchQuery}`), {headers: headers(), signal: controller.signal})
      .then(r => { if (!controller.signal.aborted) setResult(r.data.data); })
      .catch(e => { if (!controller.signal.aborted) { setResult({items: [], total_item_count: 0}); setError('loadError'); } })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [page, detailedSearchQuery, refresh, network]);
  useEffect(() => {
    if (!dirty) return;
    const guard = e => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [dirty]);
  const close = () => { if (!saving) { if (dirty) setDiscard(true); else setEditing(null); } };
  const save = async () => {
    if (saving || !dirty || (minutes !== null && (!Number.isFinite(minutes) || minutes < 0 || minutes > 1000000000))) return;
    setSaving(true); setSaveError(''); setSaveErrorDetail('');
    try {
      await axios.patch(url(`/${editing.id}/standard-time`), {standard_time: minutes}, {headers: headers()});
      setEditing(null); setRefresh(n => n + 1); onSaved?.();
      window.App.toastShow('success', label('saved'));
    } catch (e) {
      const status = e.response?.status;
      setSaveError(status === 401 ? 'sessionExpired' : status === 403 ? 'accessDenied' : status === 404 ? 'jobNotFound' : status === 400 || status === 422 ? 'invalidStandardTime' : 'saveError');
      // Validation responses contain user-facing field errors; never expose server traces.
      const fields = e.response?.data?.data || e.response?.data?.errors;
      const details = (status === 400 || status === 422) && fields && typeof fields === 'object'
        ? Object.values(fields).flat().filter(value => typeof value === 'string') : [];
      setSaveErrorDetail(details.length ? [...new Set(details)].join(' ') : status ? `HTTP ${status}` : '');
    }
    finally { setSaving(false); }
  };
  const date = value => value ? window.App.formatDateValue(value) : '—';
  return <>
    {error && <div><Message severity="error" text={label(error)}/><Button label={label('retry')} onClick={() => setRefresh(n => n + 1)}/></div>}
    <DataTable value={result.items} dataKey="id" lazy paginator rows={page.rows} first={page.first}
      totalRecords={result.total_item_count} loading={loading} emptyMessage={t('noResults')}
      rowsPerPageOptions={[10, 20, 50, 100]} onPage={e => setPage(p => ({...p, first: e.first, rows: e.rows}))}
      sortField={page.sortField} sortOrder={page.sortOrder} onSort={e => setPage(p => ({...p, first: 0, sortField: e.sortField, sortOrder: e.sortOrder}))}
      responsiveLayout="scroll" stripedRows autoLayout size={localStorage.getItem('scale')}>
      <Column field="id" header={label('id')} sortable body={job => <Button className="p-button-link" label={`#${job.id}`} onClick={() => onJobClick(job)}/>}/>
      <Column field="production_item.name" header={label('task')}/>
      <Column field="project_item_name" header={label('partName')} body={job => job.project_item_name || '—'}/>
      <Column field="project_number" header={t('columnProjectNumber')} sortable/>
      <Column field="technology.name" header={label('technology')}/>
      <Column header={label('assignee')} body={job => job.employee?.name || job.subcontractor?.name || '—'}/>
      <Column field="machine.name" header={label('machine')}/>
      <Column field="quantity" header={t('quantity')} sortable body={job => `${job.quantity ?? '—'} ${job.unit?.name || ''}`}/>
      <Column field="status" header={t('status')} sortable body={job => renderStatus ? renderStatus(job.status) : job.status}/>
      <Column field="deadline" header={label('deadline')} sortable body={job => date(job.deadline)}/>
      <Column field="scheduled_from" header={label('scheduled')} sortable body={job => `${date(job.scheduled_from)} – ${date(job.scheduled_until)}`}/>
      <Column field="standard_time" header={label('minutes')} sortable body={job => <span style={{whiteSpace: 'nowrap'}}>{job.standard_time ?? '—'} {canEdit && <Button icon="pi pi-pencil" className="p-button-text" aria-label={`${label('edit')} #${job.id}`} tooltip={label('edit')} onClick={() => {setEditing(job); setMinutes(job.standard_time ?? null); setSaveError(''); setSaveErrorDetail('');}}/>}</span>}/>
      <Column field="production_item.external_reference" header={t('externalReference')}/>
      <Column field="note" header={label('note')}/>
    </DataTable>
    <Dialog visible={!!editing} header={`${label('edit')} #${editing?.id || ''}`} modal onHide={close} closable={!saving} closeOnEscape={!saving} style={{width: '30rem', maxWidth: '95vw'}}
      footer={<><Button label={t('cancel')} className="p-button-text" disabled={saving} onClick={close}/><Button label={t('save')} loading={saving} disabled={!dirty || saving} onClick={save}/></>}>
      <p>{editing?.production_item?.name} · {editing?.project_number}</p>
      <div className="p-field"><label htmlFor="job-standard-minutes">{label('minutes')}</label><InputNumber inputId="job-standard-minutes" value={minutes} onValueChange={e => setMinutes(e.value)} min={0} max={1000000000} maxFractionDigits={3} locale={i18n.language} disabled={saving}/></div>
      <p>{label('help')}</p>
      {saveError && <Message severity="error" text={[label(saveError), saveErrorDetail].filter(Boolean).join(' ')}/>}
    </Dialog>
    <Dialog visible={discard} header={label('discard')} modal onHide={() => setDiscard(false)} footer={<><Button label={t('cancel')} onClick={() => setDiscard(false)}/><Button label={label('discard')} onClick={() => {setDiscard(false); setEditing(null);}}/></>}><p>{label('discardHelp')}</p></Dialog>
  </>;
}
